import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import dayjs from 'dayjs';

import { Checkbox } from 'components/Checkbox';
import { EmailComposer, EmailComposerConfig } from 'components/EmailComposer';
import ExpandableContent from 'components/ExpandableContent';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { EmailRecipient } from 'components/input/EmailInput/types';
import { Divider, Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography/Typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { EmailMode } from 'types/EmailMode';
import { TemplateType } from 'types/EmailTemplates';
import { EmailTemplate } from 'types/EmailTemplates';
import { NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { checkCanReplyInThread } from 'utils/canReplyInThread';
import { nl2br } from 'utils/getJoditEditorHelpers';

import { CarrierSOPTextInput } from './CarrierCheckIn';
import ThreadSubjectAutocomplete from './ThreadSubjectAutocomplete';
import { CarrierCheckInInputs, CheckInName, TimeUnits } from './types';
import { calculateSendTimeForCheckIn } from './util';

export interface CarrierCheckInDetailsFormProps {
  name: CheckInName;
  label: string;
  control: Control<any>;
  disabled?: boolean;
  isApptInPast?: boolean;
  load: NormalizedLoad;
}

export default function CarrierCheckInDetailsForm({
  name,
  label,
  control,
  disabled = false,
  isApptInPast = false,
  load,
}: CarrierCheckInDetailsFormProps) {
  const {
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const formErrors = errors as FieldErrors<CarrierCheckInInputs>;

  const {
    currentState: {
      threadId,
      threadItemId,
      threadReferences,
      inReplyTo,
      drumkitPlatform,
      inboxEmailAddress,
      threadFrom,
      threadTo,
      threadCc,
    },
  } = useContext(SidebarStateContext);

  const [emailSendTime, setEmailSendTime] = useState<Maybe<string>>(null);
  const [isEmailDraftOpen, setIsEmailDraftOpen] = useState(false);

  const pickupTime = dayjs(load.pickup.apptStartTime);
  const dropoffTime = dayjs(load.consignee.apptStartTime);

  const duration = useWatch({ control, name: `${name}.duration` });
  const timeUnit = useWatch({ control, name: `${name}.timeUnit` });
  const checkIn = useWatch({ control, name: `${name}.checkIn` });
  const frequency = useWatch({ control, name: `${name}.frequency` });
  const emailMode =
    useWatch({ control, name: `${name}.emailMode` }) || EmailMode.New;
  const recipients = useWatch({ control, name: `${name}.recipients` });

  const canReplyInThread = checkCanReplyInThread(
    drumkitPlatform,
    threadId,
    threadItemId,
    threadReferences,
    inReplyTo
  );

  useEffect(() => {
    setIsEmailDraftOpen(checkIn);
    // Set the "from" field when email draft opens
    if (checkIn && inboxEmailAddress) {
      setValue(`${name}.from`, inboxEmailAddress);
    }
  }, [checkIn, inboxEmailAddress, name, setValue]);

  // Initial recipients for new email mode
  const initialRecipients: EmailRecipient[] = useMemo(() => {
    if (load.carrier.email) {
      return [
        {
          email: load.carrier.email,
          name: load.carrier.name || 'Carrier',
        },
      ];
    }
    return [];
  }, [load.carrier.email, load.carrier.name]);

  const handleEmailModeChange = (newMode: EmailMode) => {
    setValue(`${name}.emailMode`, newMode);

    // When switching to reply mode, populate recipients and threading fields
    if (newMode === EmailMode.Reply && canReplyInThread) {
      const threadParticipants = [
        ...(threadFrom ? [threadFrom] : []),
        ...threadTo,
        ...threadCc,
      ];

      const uniqueParticipants = [...new Set(threadParticipants)].filter(
        (email) => email && email !== inboxEmailAddress
      );
      setValue(`${name}.recipients`, uniqueParticipants);

      // Store threading fields for reply mode
      if (threadId) {
        setValue(`${name}.threadID`, threadId);
      }
      if (threadItemId) {
        setValue(`${name}.threadItemID`, threadItemId);
      }
      if (threadReferences) {
        setValue(`${name}.threadReferences`, threadReferences);
      }
      if (inReplyTo && inReplyTo.length > 0) {
        setValue(`${name}.inReplyTo`, inReplyTo);
      }
      // Clear replyToSubject when switching to Reply mode
      setValue(`${name}.replyToSubject`, undefined);
    } else if (newMode === EmailMode.ReplyToSubject) {
      // When switching to ReplyToSubject mode, clear current thread fields
      // User will need to enter the subject manually
      setValue(`${name}.threadID`, undefined);
      setValue(`${name}.threadItemID`, undefined);
      setValue(`${name}.threadReferences`, undefined);
      setValue(`${name}.inReplyTo`, undefined);

      // Clear replyToSubject if switching from another mode (user will enter it)
      if (!getValues(`${name}.replyToSubject`)) {
        setValue(`${name}.replyToSubject`, '');
      }
    } else if (newMode === EmailMode.New) {
      // When switching to new email mode, restore carrier email and clear threading fields
      const carrierEmail = load.carrier.email;
      if (carrierEmail && (!recipients || recipients.length === 0)) {
        setValue(`${name}.recipients`, [carrierEmail]);
      }
      // Clear threading fields when switching to new email mode
      setValue(`${name}.threadID`, undefined);
      setValue(`${name}.threadItemID`, undefined);
      setValue(`${name}.threadReferences`, undefined);
      setValue(`${name}.inReplyTo`, undefined);
      setValue(`${name}.replyToSubject`, undefined);
    }
  };

  // Auto-switch to new email mode if reply becomes unavailable (but not for ReplyToSubject)
  useEffect(() => {
    if (emailMode === EmailMode.Reply && !canReplyInThread) {
      setValue(`${name}.emailMode`, EmailMode.New);
    }
  }, [emailMode, canReplyInThread, name, setValue]);

  // Populate recipients and threading fields when emailMode is set to reply and threading is available
  useEffect(() => {
    if (emailMode === EmailMode.Reply && canReplyInThread && checkIn) {
      // Populate recipients if they're not already set
      const currentRecipients = getValues(`${name}.recipients`);
      if (!currentRecipients || currentRecipients.length === 0) {
        const threadParticipants = [
          ...(threadFrom ? [threadFrom] : []),
          ...threadTo,
          ...threadCc,
        ];

        const uniqueParticipants = [...new Set(threadParticipants)].filter(
          (email) => email && email !== inboxEmailAddress
        );
        setValue(`${name}.recipients`, uniqueParticipants);
      }

      // Only set threading fields if they're not already set
      const currentThreadID = getValues(`${name}.threadID`);
      const currentThreadItemID = getValues(`${name}.threadItemID`);

      if (!currentThreadID && threadId) {
        setValue(`${name}.threadID`, threadId);
      }
      if (!currentThreadItemID && threadItemId) {
        setValue(`${name}.threadItemID`, threadItemId);
      }
      if (threadReferences) {
        setValue(`${name}.threadReferences`, threadReferences);
      }
      if (inReplyTo && inReplyTo.length > 0) {
        setValue(`${name}.inReplyTo`, inReplyTo);
      }
    }
  }, [
    emailMode,
    canReplyInThread,
    checkIn,
    threadId,
    threadItemId,
    threadReferences,
    inReplyTo,
    threadFrom,
    threadTo,
    threadCc,
    inboxEmailAddress,
    name,
    setValue,
    getValues,
  ]);

  const emailComposerConfig: EmailComposerConfig = useMemo(
    () => ({
      templateType: TemplateType.CARRIER,
      loadID: load.ID!,
      freightTrackingId: load.freightTrackingID,
      extractTemplates: () => [], // Carrier check-ins don't use template selection
      requireSubject: checkIn,
      showSubjectForNewEmailOnly: true,
      requireToEmail: checkIn,
      fieldNamePrefix: name,
      fieldNameMap: {
        to: 'recipients',
        emailBody: 'emailDraft',
      },
      sendEmail: async () => {
        // No-op - form submission is handled by parent
        return { isOk: () => true, error: null } as any;
      },
      copyEmail: async () => ({ success: true }),
      disabled,
    }),
    [load.ID, load.freightTrackingID, checkIn, disabled, name]
  );

  // Custom mode selector (Select dropdown)
  const modeSelectorComponent = (
    <Controller
      name={`${name}.emailMode`}
      control={control}
      render={({ field }) => (
        <div className='mb-2'>
          <Label name={`${name}.emailMode`} hideAIHint={true}>
            Send as:
          </Label>
          <Select
            onValueChange={(value) => {
              const mode = value as EmailMode;
              field.onChange(mode);
              handleEmailModeChange(mode);
            }}
            value={field.value || EmailMode.New}
            disabled={disabled}
          >
            <SelectTrigger className='w-full mt-1'>
              <SelectValue placeholder='New Email' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EmailMode.New}>New Email</SelectItem>
              <SelectItem value={EmailMode.Reply} disabled={!canReplyInThread}>
                Reply to current thread
              </SelectItem>
              <SelectItem value={EmailMode.ReplyToSubject}>
                Reply to thread by subject
              </SelectItem>
            </SelectContent>
          </Select>
          {!canReplyInThread && (
            <Typography
              variant='body-xs'
              className='text-neutral-600 italic mt-1'
            >
              Reply to thread is only available when the Drumkit Sidebar is open
              on an email thread.
            </Typography>
          )}
          {emailMode === EmailMode.ReplyToSubject && (
            <ThreadSubjectAutocomplete name={name} disabled={disabled} />
          )}
        </div>
      )}
    />
  );

  useEffect(() => {
    if (checkIn) {
      const sendTime = calculateSendTimeForCheckIn(
        name,
        duration,
        timeUnit,
        frequency,
        isApptInPast,
        pickupTime,
        dropoffTime,
        load
      );
      setEmailSendTime(sendTime);
    } else {
      setEmailSendTime(null);
    }
  }, [
    checkIn,
    duration,
    timeUnit,
    frequency,
    name,
    pickupTime,
    dropoffTime,
    isApptInPast,
    load,
  ]);

  const renderHeaderSection = () => {
    if (disabled || !checkIn || !emailSendTime) return null;

    const isInvalidAppt =
      (name === CheckInName.Pickup || name === CheckInName.Dropoff) &&
      isApptInPast;

    const showDuration =
      !isInvalidAppt &&
      name !== CheckInName.InTransit &&
      name !== CheckInName.PodCollection;

    // Show duration input for Pickup/Dropoff even when appointment is in the past
    const showDurationInput =
      showDuration ||
      (name === CheckInName.Pickup && isApptInPast) ||
      (name === CheckInName.Dropoff && isApptInPast);

    const renderLabel = () => {
      switch (name) {
        case CheckInName.Dispatch:
        case CheckInName.Pickup:
        case CheckInName.Dropoff:
          if (isApptInPast) {
            return 'How long after now';
          }
          return null;
        case CheckInName.AfterPickup:
          return `How long after ${isApptInPast ? 'now' : 'pickup appt'}`;
        case CheckInName.AfterDropoff:
          return `How long after ${isApptInPast ? 'now' : 'dropoff appt'}`;
        default:
          return null;
      }
    };

    const labelText = renderLabel();

    return (
      <Grid cols='2' gap='sm' className='w-full mx-0'>
        {labelText && (
          <div className='col-span-2'>
            <Label name=''>{labelText}</Label>
          </div>
        )}

        {showDurationInput && (
          <CarrierSOPTextInput
            name={`${name}.duration`}
            label=''
            inputType='number'
            step={1}
            hideAIHint={true}
            readOnly={disabled}
          />
        )}

        {name === CheckInName.InTransit && (
          <CarrierSOPTextInput
            name={`${name}.frequency`}
            label=''
            inputType='number'
            step={1}
            hideAIHint={true}
            readOnly={disabled}
          />
        )}

        {(showDuration ||
          name === CheckInName.InTransit ||
          (name === CheckInName.Pickup && isApptInPast) ||
          (name === CheckInName.Dropoff && isApptInPast)) && (
          <Controller
            name={`${name}.timeUnit`}
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={disabled}
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder={TimeUnits.Hours} />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(TimeUnits).map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )}
      </Grid>
    );
  };

  const renderEmailSendTime = () => {
    const isInTransit = name === CheckInName.InTransit;

    return (
      <div className='mb-4'>
        {isInTransit ? (
          <Typography variant='body-sm'>
            An email will be sent every {frequency} {timeUnit} between{' '}
            {pickupTime.format('MM/DD HH:mm')} and{' '}
            {dropoffTime.format('MM/DD HH:mm')}.
          </Typography>
        ) : (
          <Flex gap='xs' align='center'>
            <Typography variant='body-sm'>An email will be sent at:</Typography>
            <Typography variant='body-xs' weight='semibold'>
              {` ${emailSendTime}`}
            </Typography>
          </Flex>
        )}
      </div>
    );
  };

  return (
    <Grid gap='sm' className='mb-2 w-full mx-0'>
      <Divider variant='strong' size='md' className='my-1' />

      <Controller
        name={`${name}.checkIn`}
        control={control}
        render={({ field }) => (
          <Checkbox
            label={label}
            labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 hover:underline font-medium tracking-wide'
            checked={field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
            }}
            disabled={disabled}
          />
        )}
      />

      {renderHeaderSection()}

      {emailSendTime && renderEmailSendTime()}

      <ExpandableContent
        showText='Show email draft'
        hideText='Hide email draft'
        open={isEmailDraftOpen}
        onOpenChange={setIsEmailDraftOpen}
        forceOpen={
          checkIn &&
          (formErrors?.[name]?.['recipients']?.message ||
            (emailMode === EmailMode.New &&
              formErrors?.[name]?.['subject']?.message) ||
            formErrors?.[name]?.['emailDraft']?.message)
        }
        className='mb-0 gap-0'
      >
        {isEmailDraftOpen && (
          <>
            <EmailComposer
              config={emailComposerConfig}
              modeSelectorComponent={modeSelectorComponent}
              useExternalFormContext={true}
              externalControl={control}
              initialRecipients={initialRecipients}
              getDefaultSubject={(template?: EmailTemplate) =>
                template?.subject || ''
              }
              getDefaultEmailBody={(template?: EmailTemplate) =>
                nl2br(template?.body || '')
              }
            />

            {name === CheckInName.PodCollection && (
              <Typography variant='body-xs' className='text-neutral-600 italic'>
                {load.status.toLowerCase() === 'delivered' ? (
                  <>
                    Email will be sent <b>immediately</b> since load status is
                    marked as "delivered" in Tai.
                  </>
                ) : (
                  <>
                    Email will be sent to the recipient when{' '}
                    <b>Tai's load status is updated</b> to "delivered"
                  </>
                )}
              </Typography>
            )}
          </>
        )}
      </ExpandableContent>
    </Grid>
  );
}
